/**
 * Configurator Tool Page
 *
 * Configure IoT device parameters and technical settings on the assembly line.
 * Tool color: amber
 *
 * Workflow:
 * 1. Select technology and configuration profile
 * 2. Manage serial ports and detect devices
 * 3. Apply configuration and view results
 */

import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ExternalLink,
  Loader2,
  Plus,
  Settings,
  Zap,
} from 'lucide-react';
import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { configurationProfileApi } from '@/api/production/configuration-profiles';
import { type ITechnology, technologyApi } from '@/api/production/technologies';
import { ConsoleDisplay } from '@/components/production/ConsoleDisplay';
import { TechnologySearchForm } from '@/components/production/TechnologySearchForm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useConfiguration } from '@/hooks/production/useConfiguration';
import type { ISerialPort } from '@/hooks/useSerialPort';
import { cn } from '@/lib/utils';
import type { IConfigurationLog } from '@/types/production/configuration-log';
import type { IConfigurationProfile } from '@/types/production/configuration-profile';

// ============================================
// Types
// ============================================

// ============================================
// Status Badge Component
// ============================================

function DeviceStatusBadge({
  status,
}: {
  status: 'fully_identified' | 'partially_identified' | 'not_identified';
}) {
  const config = {
    fully_identified: {
      label: 'Identificado',
      className: 'bg-green-100 text-green-800',
      dotClass: 'bg-green-500',
    },
    partially_identified: {
      label: 'Parcialmente Identificado',
      className: 'bg-yellow-100 text-yellow-800',
      dotClass: 'bg-yellow-500',
    },
    not_identified: {
      label: 'Não Identificado',
      className: 'bg-red-100 text-red-800',
      dotClass: 'bg-red-500',
    },
  };

  const { label, className, dotClass } = config[status];

  return (
    <div className="flex items-center gap-2">
      <div className={cn('h-2 w-2 rounded-full', dotClass)} />
      <span className={cn('text-xs font-medium', className)}>{label}</span>
    </div>
  );
}

// ============================================
// Detected Devices Table
// ============================================

function DetectedDevicesTable({
  devices,
}: {
  devices: Array<{
    port: ISerialPort;
    equipment?: {
      serial?: string;
      imei?: string;
      iccid?: string;
      firmware?: string;
    };
    status: 'fully_identified' | 'partially_identified' | 'not_identified';
  }>;
}) {
  if (devices.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center">
        <AlertCircle className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">Nenhum dispositivo detectado</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Conecte uma porta serial e aguarde a detecção automática
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Status</TableHead>
            <TableHead>Serial</TableHead>
            <TableHead>IMEI</TableHead>
            <TableHead>ICCID</TableHead>
            <TableHead>Firmware</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {devices.map((device, index) => (
            <TableRow key={device.equipment?.serial || index}>
              <TableCell>
                <DeviceStatusBadge status={device.status} />
              </TableCell>
              <TableCell className="font-mono text-sm">
                {device.equipment?.serial ?? '--'}
              </TableCell>
              <TableCell className="font-mono text-sm">{device.equipment?.imei ?? '--'}</TableCell>
              <TableCell className="font-mono text-sm">{device.equipment?.iccid ?? '--'}</TableCell>
              <TableCell className="font-mono text-sm">
                {device.equipment?.firmware ?? '--'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ============================================
// Configured Devices Table (expandable)
// ============================================

interface ConfiguredDevicesTableProps {
  devices: IConfigurationLog[];
  onViewLog?: (logId: string) => void;
}

function parseMismatchResponse(response: string | null | undefined): {
  expected: string;
  received: string;
} {
  if (!response) return { expected: '–', received: '–' };
  const expectedMatch = response.match(/expected:\s*(.+?)(?:,\s*received:|$)/);
  const receivedMatch = response.match(/received:\s*(.+)$/);
  return {
    expected: expectedMatch?.[1]?.trim() ?? '–',
    received: receivedMatch?.[1]?.trim() ?? '–',
  };
}

function ConfiguredDevicesTable({ devices, onViewLog }: ConfiguredDevicesTableProps) {
  const [expandedId, setExpandedId] = React.useState<string | null>(null);

  if (devices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <CheckCircle className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium text-muted-foreground mb-2">
          Nenhum equipamento configurado
        </h3>
        <p className="text-sm text-muted-foreground max-w-md">
          Complete as etapas anteriores para visualizar os equipamentos configurados.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {devices.map((device, index) => {
        const uniqueKey = `${device.id}-${index}`;
        const isExpanded = expandedId === uniqueKey;

        const messages = device.messages ?? [];
        const writes = messages.filter((m) => m.key.startsWith('w_'));
        const reads = messages.filter(
          (m) =>
            !m.key.startsWith('w_') &&
            !m.key.startsWith('mismatch_') &&
            m.key !== 'validation_result',
        );
        const mismatches = messages.filter((m) => m.key.startsWith('mismatch_'));
        const duration =
          device.end_time && device.init_time
            ? ((device.end_time - device.init_time) / 1000).toFixed(1)
            : null;

        return (
          <div key={uniqueKey} className="rounded-lg border">
            {/* Header — clickable */}
            <button
              type="button"
              className="w-full p-4 text-left hover:bg-muted/50 transition-colors bg-transparent border-0"
              onClick={() => setExpandedId(isExpanded ? null : uniqueKey)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {device.status ? (
                    <Badge variant="success-subtle" className="gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Configurado
                    </Badge>
                  ) : (
                    <Badge variant="destructive-subtle" className="gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Falhou
                    </Badge>
                  )}
                  <span className="font-mono text-sm font-medium">{device.equipment.serial}</span>
                  <Badge variant="outline" className="text-xs">
                    {writes.length}w + {reads.length}r
                  </Badge>
                  {mismatches.length > 0 && (
                    <Badge variant="destructive-subtle" className="text-xs gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {mismatches.length} divergência(s)
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {duration && <span className="text-xs text-muted-foreground">{duration}s</span>}
                  <span className="text-xs text-muted-foreground">
                    {new Date(device.created_at).toLocaleString('pt-BR')}
                  </span>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 text-muted-foreground transition-transform duration-200',
                      isExpanded && 'rotate-180',
                    )}
                  />
                </div>
              </div>
            </button>

            {/* Expanded content */}
            {isExpanded && (
              <div className="border-t p-4 space-y-5">
                {/* Write commands */}
                {writes.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Comandos escritos ({writes.length})
                    </p>
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[30%]">Parâmetro</TableHead>
                            <TableHead className="w-[35%]">Comando</TableHead>
                            <TableHead className="w-[35%]">Confirmação</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {writes.map((m) => (
                            <TableRow key={m.key}>
                              <TableCell className="text-sm font-medium">
                                {m.key.replace(/^w_/, '')}
                              </TableCell>
                              <TableCell className="font-mono text-xs text-blue-600">
                                {m.request.trim()}
                              </TableCell>
                              <TableCell className="font-mono text-xs">
                                {m.response ? (
                                  <span className="text-green-600">{String(m.response)}</span>
                                ) : (
                                  <span className="text-gray-400">–</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Read / verification commands */}
                {reads.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Verificação ({reads.length})
                    </p>
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[20%]">Parâmetro</TableHead>
                            <TableHead className="w-[25%]">Comando</TableHead>
                            <TableHead className="w-[55%]">Resposta do dispositivo</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {reads.map((m) => (
                            <TableRow key={m.key}>
                              <TableCell className="text-sm font-medium">{m.key}</TableCell>
                              <TableCell className="font-mono text-xs text-blue-600">
                                {m.request.trim()}
                              </TableCell>
                              <TableCell className="font-mono text-xs">
                                {m.response ? (
                                  <span className="text-green-700">{String(m.response)}</span>
                                ) : (
                                  <span className="text-gray-400">Sem resposta</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Mismatches */}
                {mismatches.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-red-600">
                      Divergências encontradas ({mismatches.length})
                    </p>
                    <div className="rounded-md border border-red-200">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[30%]">Parâmetro</TableHead>
                            <TableHead className="w-[35%]">Esperado (perfil)</TableHead>
                            <TableHead className="w-[35%]">Recebido (dispositivo)</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {mismatches.map((m) => {
                            const param = m.key.replace(/^mismatch_/, '');
                            const { expected, received } = parseMismatchResponse(m.response);
                            return (
                              <TableRow key={m.key}>
                                <TableCell className="text-sm font-medium">{param}</TableCell>
                                <TableCell className="font-mono text-xs text-green-700">
                                  {expected}
                                </TableCell>
                                <TableCell className="font-mono text-xs text-red-600">
                                  {received === 'undefined' ? (
                                    <span className="text-gray-400">Sem resposta</span>
                                  ) : (
                                    received
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* View log link */}
                {onViewLog && (
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewLog(device.id);
                      }}
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      Ver log completo
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================
// Main Page Component
// ============================================

export default function ConfiguratorPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const technologyId = searchParams.get('technology_id');
  const profileId = searchParams.get('configuration_profile_id');

  // State
  const [selectedTechnology, setSelectedTechnology] = React.useState<ITechnology | null>(null);
  const [selectedProfile, setSelectedProfile] = React.useState<IConfigurationProfile | null>(null);
  const [consoleLogs, setConsoleLogs] = React.useState<string[]>([]);

  // Fetch technology if ID is in URL
  const { data: technologyData } = useQuery({
    queryKey: ['technology', technologyId],
    queryFn: () => technologyApi.findOne(technologyId!),
    enabled: !!technologyId,
  });

  // Fetch profile if ID is in URL
  const { data: profileData } = useQuery({
    queryKey: ['configuration-profile', profileId],
    queryFn: () => configurationProfileApi.findOne(profileId!),
    enabled: !!profileId,
  });

  // Set technology from URL param
  React.useEffect(() => {
    if (technologyData) {
      setSelectedTechnology(technologyData);
    }
  }, [technologyData]);

  // Set profile from URL param
  React.useEffect(() => {
    if (profileData) {
      setSelectedProfile(profileData);
    }
  }, [profileData]);

  const date = new Date();

  const addLog = React.useCallback((message: string) => {
    const timestamp = new Date().toLocaleTimeString('pt-BR');
    const logMessage = `[${timestamp}] ${message}`;
    setConsoleLogs((prev) => [...prev, logMessage]);
    console.log('[Configurator]', logMessage);
  }, []);

  const clearLogs = React.useCallback(() => {
    setConsoleLogs([]);
    console.log('[Configurator] Logs cleared');
  }, []);

  const formatResponseValue = React.useCallback((value: unknown): string => {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (Array.isArray(value)) {
      return value.join(',');
    }
    if (typeof value === 'object') {
      const values = Object.values(value).filter((v) => v !== null && v !== undefined);
      if (values.length === 1 && typeof values[0] === 'string') {
        return values[0];
      }
      if (values.length === 1) {
        if (typeof values[0] === 'number' || typeof values[0] === 'boolean') {
          return String(values[0]);
        }
        return JSON.stringify(values[0]);
      }
      return JSON.stringify(value);
    }
    return String(value);
  }, []);

  const handleTechnologyChange = React.useCallback(
    (tech: ITechnology | null) => {
      setSelectedTechnology(tech);
      setSelectedProfile(null);
      clearLogs();
      if (tech) {
        addLog(`Tecnologia selecionada: ${tech.name.brand}`);
      }
    },
    [addLog, clearLogs],
  );

  const handleProfileChange = React.useCallback(
    (profile: IConfigurationProfile | null) => {
      setSelectedProfile(profile);
      if (profile) {
        addLog(`Perfil de configuração selecionado: ${profile.name}`);
      }
    },
    [addLog],
  );

  const {
    configured,
    detected,
    configure,
    requestPort,
    isConfiguring,
    isDetecting,
    toggleAutoConfiguration,
    autoConfigurationEnabled,
    redirectToCheckEnabled,
    toggleRedirectToCheck,
    redirectToCheck,
  } = useConfiguration({
    technology: selectedTechnology,
    configurationProfile: selectedProfile,
  });

  // Log when devices are detected
  const detectedSerialsRef = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    const currentSerials = new Set(
      detected.filter((d) => d.equipment?.serial).map((d) => d.equipment?.serial!),
    );
    const newSerials = Array.from(currentSerials).filter(
      (serial) => !detectedSerialsRef.current.has(serial),
    );

    newSerials.forEach((serial) => {
      addLog(`Dispositivo detectado: ${serial}`);
    });

    detectedSerialsRef.current = currentSerials;

    if (detected.length === 0 && detectedSerialsRef.current.size > 0) {
      addLog('Nenhum dispositivo detectado');
      detectedSerialsRef.current.clear();
    }
  }, [detected, addLog]);

  // Log when configuration completes
  const lastConfiguredLengthRef = React.useRef(0);
  React.useEffect(() => {
    const newCount = configured.length - lastConfiguredLengthRef.current;
    if (newCount > 0) {
      const newDevices = configured.slice(-newCount);
      newDevices.forEach((device) => {
        if (device.status) {
          addLog(`✓ ${device.equipment.serial} configurado com sucesso`);
        } else {
          addLog(`✗ Falha ao configurar ${device.equipment.serial}`);
        }
        // Log messages from configuration (both success and failure)
        device.messages?.forEach((msg) => {
          addLog(`  → ${msg.key}: ${msg.request}`);
          if (msg.response) {
            const responseStr = formatResponseValue(msg.response);
            if (responseStr) {
              addLog(`  ← ${responseStr}`);
            }
            console.log(`[Configurator] Device Response - ${msg.key}:`, msg.response);
          }
        });
      });
    }
    lastConfiguredLengthRef.current = configured.length;
  }, [configured, addLog, formatResponseValue]);

  // Redirect to check configuration page (defined first - no callback dependencies)
  const handleRedirectToCheck = React.useCallback(() => {
    if (selectedTechnology && selectedProfile) {
      navigate(
        `/production/tools/check-configuration?technology_id=${selectedTechnology.id}&configuration_profile_id=${selectedProfile.id}`,
      );
    }
  }, [navigate, selectedTechnology, selectedProfile]);

  // Handle viewing configuration log details
  const handleViewLog = React.useCallback(
    (logId: string) => {
      navigate(`/production/logs/configurator/${logId}`);
    },
    [navigate],
  );

  // Configure devices with logging
  const handleConfigure = React.useCallback(async () => {
    addLog('Iniciando configuração...');
    addLog(`Configurando ${detected.length} dispositivo(s)`);
    await configure(detected, selectedProfile!);
  }, [configure, detected, selectedProfile, addLog]);

  return (
    <div className="container mx-auto pb-8 px-4 space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-base font-semibold leading-7 text-gray-900">Configurador</h1>
        <p className="mt-2 text-sm text-gray-700">
          Ferramenta para configurar <span className="text-gray-900">equipamentos IoT.</span> Data
          de hoje: <time dateTime={date.toLocaleDateString()}>{date.toLocaleDateString()}</time>
        </p>
      </div>

      {/* Step 1: Technology and Profile Selection */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <Badge
              variant="default"
              className="w-8 h-8 rounded-full flex items-center justify-center p-0 bg-black text-white"
            >
              1
            </Badge>
            <div className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-black" />
              <CardTitle className="text-xl">
                Definição da tecnologia e perfil de configuração
              </CardTitle>
            </div>
          </div>
          <CardDescription className="ml-11">
            Escolha a tecnologia e o perfil de configuração para os equipamentos
          </CardDescription>
        </CardHeader>
        <CardContent className="ml-11 space-y-6">
          <TechnologySearchForm
            technology={selectedTechnology}
            configurationProfile={selectedProfile}
            showConfigurationProfile={true}
            showSerialNumber={false}
            onTechnologyChange={handleTechnologyChange}
            onConfigurationProfileChange={handleProfileChange}
          />

          <div className="flex items-center gap-2">
            <Switch checked={autoConfigurationEnabled} onCheckedChange={toggleAutoConfiguration} />
            <Label>Ativar configuração automática</Label>
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={redirectToCheckEnabled} onCheckedChange={toggleRedirectToCheck} />
            <Label>Ativar redirecionamento para checagem</Label>
          </div>
        </CardContent>
      </Card>

      {/* Step 2: Port Management */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <Badge
              variant={selectedTechnology ? 'default' : 'secondary'}
              className="w-8 h-8 rounded-full flex items-center justify-center p-0 bg-black text-white"
            >
              2
            </Badge>
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-black" />
              <CardTitle className="text-xl">Gerenciamento de Portas</CardTitle>
            </div>
          </div>
          <CardDescription className="ml-11">
            Visualize e configure os equipamentos conectados às portas seriais
          </CardDescription>
        </CardHeader>
        {selectedTechnology ? (
          <CardContent className="ml-11 space-y-6">
            <DetectedDevicesTable devices={detected} />

            <Separator />

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleConfigure}
                  disabled={
                    !selectedProfile ||
                    detected.length === 0 ||
                    isConfiguring ||
                    (isDetecting && detected.length === 0)
                  }
                  className="min-w-35 bg-black hover:bg-gray-800 text-white"
                >
                  {isDetecting && detected.length === 0 ? (
                    <>
                      <Loader2 className="animate-spin mr-2 h-4 w-4" />
                      Detectando
                    </>
                  ) : isConfiguring ? (
                    <>
                      <Loader2 className="animate-spin mr-2 h-4 w-4" />
                      Configurando
                    </>
                  ) : (
                    <>
                      <Settings className="mr-2 h-4 w-4" />
                      Configurar
                    </>
                  )}
                </Button>

                {!selectedProfile && (
                  <p className="text-sm text-muted-foreground self-center">
                    Selecione um perfil de configuração para continuar
                  </p>
                )}
              </div>

              <Button variant="outline" onClick={requestPort} className="min-w-35">
                <Plus className="mr-2 h-4 w-4" />
                Nova Porta
              </Button>
            </div>
          </CardContent>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <CheckCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground mb-2">
              Nenhuma tecnologia selecionada
            </h3>
            <p className="text-sm text-muted-foreground max-w-md">
              Complete as etapas anteriores para visualizar os equipamentos detectados.
            </p>
          </div>
        )}
      </Card>

      {/* Step 3: Results */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <Badge
              variant={configured.length > 0 ? 'default' : 'secondary'}
              className="w-8 h-8 rounded-full flex items-center justify-center p-0 bg-black text-white"
            >
              3
            </Badge>
            <div className="flex items-center gap-2">
              <CheckCircle
                className={`h-5 w-5 ${configured.length > 0 ? 'text-black' : 'text-muted-foreground'}`}
              />
              <CardTitle className="text-xl">Verificação e Resultados</CardTitle>
            </div>
            <Button
              variant="link"
              disabled={!configured.length}
              onClick={handleRedirectToCheck}
              className="ml-auto"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Checar configurações
            </Button>
          </div>
          <CardDescription className="ml-11">
            {configured.length > 0
              ? `${configured.length} equipamento(s) configurado(s) com sucesso`
              : 'Aguardando configuração dos equipamentos'}
          </CardDescription>
        </CardHeader>
        {selectedTechnology ? (
          <CardContent className="ml-11 space-y-6">
            <ConfiguredDevicesTable devices={configured} onViewLog={handleViewLog} />

            {/* Console Output */}
            <ConsoleDisplay
              logs={consoleLogs}
              height={200}
              title="Log de Execução"
              textColor="amber"
              bgColor="gray"
              showLineNumbers={false}
              onClear={clearLogs}
            />
          </CardContent>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <CheckCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground mb-2">
              Nenhuma tecnologia selecionada
            </h3>
            <p className="text-sm text-muted-foreground max-w-md">
              Complete as etapas anteriores para visualizar os equipamentos detectados.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}

export { ConfiguratorPage };
